import { GemaCollectionProvider } from "@/components/Ramon/GemaCollection";
import { GemaExperienceProvider } from "@/components/Ramon/GemaExperience";
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PlanoV2RouteBoundary } from "./components/PlanoV2RouteBoundary";
import { NavigationBlocker } from "./components/NavigationBlocker";
import { ThemeProvider } from "./hooks/useTheme";
import { useAppHeartbeat } from "./hooks/useAppHeartbeat";
import { useProductSurveyOpening } from "./hooks/useProductSurveyOpening";
import SimpleLoading from "./components/SimpleLoading";
import ProfileSkeleton from "./components/ProfileSkeleton";
import OfflineIndicator from "./components/OfflineIndicator";
import { LiquidMercuryDefs } from "@/components/icons";
import { WaterTrackingProvider } from "@/hooks/useWaterTrackingToggle";
import { WaterTodayProvider } from "@/hooks/useWaterToday";
import { ESTRUTURA_DIA_ENABLED } from "@/lib/features";
import { AuthProvider } from "@/hooks/useAuth";
import { UserBootstrapProvider } from "@/hooks/useUserBootstrap";
import { AppBootError } from "@/components/AppBootError";
import {
  loadChatRoute,
  loadProfileRoute,
  loadProgressRoute,
} from "@/lib/routePrefetch";

// Rotas críticas (eager) — carregam no bundle principal
import AppLayout from "./components/AppLayout";
import Login from "./pages/Login";
// Réplica da tela do Plano para verificar o tour em browser real. Só é montada
// atrás de `import.meta.env.DEV` (ver a rota, mais abaixo).
import TourPreview from "./pages/TourPreview";
import PesquisaPreferenciaPreview from "./pages/PesquisaPreferenciaPreview";

// Rotas secundárias (lazy) — code splitting, carregam sob demanda
const GemaIntroductionPreview = lazy(() => import("./pages/GemaIntroductionPreview"));
const Chat = lazy(loadChatRoute);
const EstruturaDia = lazy(() => import("./pages/EstruturaDia"));
// Sistema Vivo (lazy) — o lazy() só guarda a factory; com VITE_FEATURE_DIETA_V2
// OFF a rota nunca renderiza o gate, então o chunk nunca é buscado e nenhum
// request pra /api/v2/plano acontece.
const PlanoV2HomeGate = lazy(() => import("./pages/CardapioV2/PlanoV2HomeGate"));
// O quiz do app. `lazy` porque ele carrega o port inteiro do funil (~4.700
// linhas + 1.000 de CSS + 3 fontes) e ele só é alcançado por quem virou
// cliente sem passar pelo Stripe — deixar isso no bundle inicial faria todo
// mundo pagar o download de uma tela que quase ninguém abre.
const QuizDoApp = lazy(() => import("./pages/QuizDoApp"));
// F0-4 · Personalizar plano. Mesmo padrão e mesma flag: com VITE_FEATURE_DIETA_V2
// OFF a rota cai no Home legado, o chunk nunca é buscado, e nenhum request pras
// rotas de estrutura acontece. ⚠️ NÃO confundir com `/estrutura-do-dia`
// (`EstruturaDia`, acima, flag própria) — aquela é a Fase D do motor v2.
const PersonalizarPlano = lazy(() => import("./pages/CardapioV2/PersonalizarPlano"));
const Profile = lazy(loadProfileRoute);
// T5 (10/08): `/signup` deixou de criar conta e passa a mandar pro funil, que é
// onde a conta nasce junto da venda. `pages/Signup.tsx` continua no repo, sem
// rota — voltar atrás é trocar esta linha e a de baixo, e nada mais.
const SignupRedirect = lazy(() => import("./pages/SignupRedirect"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Welcome = lazy(() => import("./pages/Welcome"));
const MeuProgresso = lazy(loadProgressRoute);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,      // 5 min — dados ficam fresh, evita re-fetch
      gcTime: 10 * 60 * 1000,         // 10 min — cache mantido mesmo após unmount
      retry: 1,                        // 1 retry (não 3) para falhar rápido
      refetchOnWindowFocus: false,     // App mobile via Median — focus é irrelevante
    },
  },
});

/** Mantém o estado de água vivo entre rotas do AppLayout, mas só habilita I/O
 * na Home e em Meu Progresso. Assim Perfil/Personalizar não leem esses dados e
 * Home → Perfil → Home não refaz o bootstrap. */
const WaterRouteProviders = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  const enabled = normalizedPath === "/home" || normalizedPath === "/meu-progresso";
  return (
    <WaterTrackingProvider enabled={enabled}>
      <WaterTodayProvider enabled={enabled}>{children}</WaterTodayProvider>
    </WaterTrackingProvider>
  );
};

const AppContent = () => {
  // PR 2: app open tracking. Hook plugado uma vez no nivel global.
  // Skip se sem session (login screen). Plan canonico: legal-faz-seu-self-review-mutable-nova.md
  useAppHeartbeat();
  useProductSurveyOpening();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <OfflineIndicator />
          <LiquidMercuryDefs />
          <BrowserRouter>
            <NavigationBlocker />
            <Suspense fallback={<SimpleLoading />}>
            <Routes>
              {import.meta.env.DEV && <Route path="/gema-apresentacao" element={<GemaIntroductionPreview />} />}
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignupRedirect />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/clubes/*" element={<Navigate to="/home" replace />} />
              <Route path="/comunidade-termos" element={<Navigate to="/home" replace />} />
              {/* ⚠️ Preview do tour — só em desenvolvimento. `import.meta.env.DEV`
                  é `false` no build de produção e o esbuild dobra a condição, então
                  esta rota não existe no bundle servido. */}
              {import.meta.env.DEV && (
                <Route path="/tour-preview" element={<TourPreview />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/pesquisa-preview" element={<PesquisaPreferenciaPreview />} />
              )}
              {/* ProtectedRoute continua gatekeeping auth. Providers de feature
                  ficam nas rotas que realmente consomem o dado. */}
              <Route
                element={
                  <ProtectedRoute>
                    <Outlet />
                  </ProtectedRoute>
                }
              >
                {/* ⚠️ FORA do AppLayout de propósito: o quiz é tela cheia, rola
                    por conta própria e a BottomNav competiria com ele. Dentro
                    do ProtectedRoute porque a rota exige sessão — é o `auth_id`
                    do JWT que diz de quem é o plano. */}
                <Route
                  path="/quiz"
                  element={
                    <Suspense fallback={<SimpleLoading />}>
                      <QuizDoApp />
                    </Suspense>
                  }
                />

                {/* AppLayout = rotas com BottomNav (Home/Profile/MeuProgresso) */}
                <Route
                  element={
                    <WaterRouteProviders>
                      <AppLayout />
                    </WaterRouteProviders>
                  }
                >
                  {/* /home: gate de runtime (200 do /api/v2/plano → Sistema
                      Vivo; 404 sem plano e indisponibilidade são estados
                      explícitos, nunca inferência de legado). O gate confirma a
                      assinatura antes de sondar (o AppLayout só barra com
                      !loading, então a janela de loading chega até aqui).
                      PlanoV2RouteBoundary: se o CHUNK do Sistema Vivo não
                      carregar, mostra retry explícito.
                      ⚠️ Não há mais ramo de flag: a Home legada foi removida no
                      sunset de 2026-08-21 e VITE_FEATURE_DIETA_V2 ficou INERTE.
                      Desligá-la não é rollback — ver src/lib/features.ts. */}
                  <Route
                    path="/home"
                    element={

                          <PlanoV2RouteBoundary
                            path="/home"
                            fallback={
                              <AppBootError onRetry={() => window.location.reload()} />
                            }
                          >
                            <Suspense fallback={<SimpleLoading />}><PlanoV2HomeGate /></Suspense>
                          </PlanoV2RouteBoundary>
                    }
                  />
                  {/* F0-4 · Personalizar plano. A proteção contra a tela de
                      ESCRITA aparecer para quem não tem estrutura v2 deixou de
                      ser a flag e passou a ser o gate: sem plano v2, o
                      PlanoV2RouteBoundary/gate não entrega a tela. */}
                  <Route
                    path="/personalizar-plano"
                    element={

                          <PlanoV2RouteBoundary
                            path="/personalizar-plano"
                            fallback={
                              <AppBootError onRetry={() => window.location.reload()} />
                            }
                          >
                            <Suspense fallback={<SimpleLoading />}><PersonalizarPlano /></Suspense>
                          </PlanoV2RouteBoundary>
                    }
                  />
                  <Route path="/profile" element={<Suspense fallback={<ProfileSkeleton />}><Profile /></Suspense>} />
                  <Route
                    path="/meu-progresso"
                    element={
                      <Suspense fallback={<SimpleLoading />}><MeuProgresso /></Suspense>
                    }
                  />
                </Route>
                {/* Chat = full-screen, sem BottomNav (padrão WhatsApp/Telegram) */}
                <Route path="/chat" element={<Chat />} />
                {/* Estrutura do Dia (motor v2, Fase D) = full-screen com CTA fixo */}
                {ESTRUTURA_DIA_ENABLED && (
                  <Route path="/estrutura-do-dia" element={<EstruturaDia />} />
                )}
              </Route>
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

const App = () => (
  <ErrorBoundary>
    <AuthProvider>
      <UserBootstrapProvider>
        <GemaExperienceProvider><GemaCollectionProvider><AppContent /></GemaCollectionProvider></GemaExperienceProvider>
      </UserBootstrapProvider>
    </AuthProvider>
  </ErrorBoundary>
);

export default App;
